// R7-5 — THE FIRST COMPONENT OUT OF App.tsx, AND IT WAS PICKED FOR A REASON.
//
// R5 and R6 moved pure laws and data. This is the first *component*, and the entry requirement for
// one is the same as it always was: it must be reachable by the gate. This one is — it takes props,
// holds no state, calls no hook, and closes over nothing in App, so `scripts/r7-components.mjs` can
// CALL it through `loadTsx` (whose stubbed JSX runtime hands back plain `{ type, props }` objects)
// and read the tree it returns. A component that needs React to run could not be checked at all.
//
// What it renders is the owner's own legal text: the Terms, the Privacy Policy and the FAQ are
// owner-authored markdown at the repo root, bundled at build time (?raw) and rendered as designed
// pages. The content is HERS — this renders it faithfully and never rewrites, summarises, or fills
// in anything.
//
// A deliberately small renderer, because the source only uses four constructs (headings, bold,
// bullets, paragraphs). It builds real React elements rather than setting HTML, so there is no
// injection surface even as the documents change — and that is the one law here worth a gate, since
// a "helpful" rewrite to dangerouslySetInnerHTML would look identical on screen.
//
// Moved VERBATIM. `headingSlug` (T3-2) stays the ONE definition, in src/lib/format.ts, shared with
// the FAQ's jump menu so a link can never point at an id that is not there.
import type { ReactNode } from "react";
import { headingSlug } from "../lib/format";

// An email link, and ONLY an email link: her 2026-10-17 ruling puts admin@12thhouse.ai in the Privacy
// Policy as a clickable address. No other scheme is drawn as a link, so the documents cannot carry one.
const MAILTO = /^\[([^\]]+)\]\(mailto:([^)\s]+@[^)\s]+)\)$/;

function renderInline(text: string, keyBase: string) {
  // **bold** and an email link are the only inline marks the documents use.
  return text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(mailto:[^)\s]+\))/g).filter(Boolean).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={`${keyBase}-b${i}`}>{part.slice(2, -2)}</strong>;
    const mail = MAILTO.exec(part);
    if (mail) return <a key={`${keyBase}-m${i}`} href={`mailto:${mail[2]}`}>{mail[1]}</a>;
    return <span key={`${keyBase}-t${i}`}>{part}</span>;
  });
}

export function Markdown({ source, headingIds = false }: { source: string; headingIds?: boolean }) {
  const blocks: ReactNode[] = [];
  const lines = String(source).replace(/\r\n/g, "\n").split("\n");
  let paragraph: string[] = [];
  let bullets: string[] = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    const text = paragraph.join(" ");
    blocks.push(<p key={`p${blocks.length}`}>{renderInline(text, `p${blocks.length}`)}</p>);
    paragraph = [];
  };
  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    blocks.push(
      <ul key={`u${blocks.length}`} className="legal-page__list">
        {items.map((b, i) => <li key={i}>{renderInline(b, `u${blocks.length}-${i}`)}</li>)}
      </ul>,
    );
    bullets = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph(); flushBullets();
      const level = heading[1].length;
      const text = heading[2];
      const key = `h${blocks.length}`;
      if (level === 1) blocks.push(<h1 key={key}>{renderInline(text, key)}</h1>);
      else if (level === 2) blocks.push(<h2 key={key} id={headingIds ? headingSlug(text) : undefined}>{renderInline(text, key)}</h2>);
      else blocks.push(<h3 key={key}>{renderInline(text, key)}</h3>);
    } else if (bullet) {
      flushParagraph();
      bullets.push(bullet[1]);
    } else if (!line.trim()) {
      flushParagraph(); flushBullets();
    } else {
      flushBullets();
      paragraph.push(line.trim());
    }
  }
  flushParagraph(); flushBullets();
  return <>{blocks}</>;
}
